import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	Area,
	Text,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";

interface Props {
	chartData: any;
	heading: any;
	page: any;
	isVisible: any;
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength, data } = props;
	console.log(data);
	const offset = 20;
	const shouldShow = index === 0 || index === dataLength - 1;
	return (
		<g>
			{shouldShow && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x}
						y2={(data[index].sales_Prev < data[index].sales_Curr ? y + offset + 10 : y - offset )}
						stroke="#0097a9"
						opacity={0.5}
					/>
					<text
						x={x - offset}
						y={(data[index].sales_Prev < data[index].sales_Curr ? y + offset + 15 : y - offset - 5)}
						fill="#0097a9"
						className="graph-label-small"
						opacity={`${page == "summary" ? 0.8 : 1}`}
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength, last, data } = props;
	const offset = 20;
	const shouldShow =
		(index === 0 && !last) || (index === dataLength - 1 && last);

	return (
		<g>
			{shouldShow && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x}
						y2={ data[index].sales_Prev < data[index].sales_Curr ? y - offset :y + offset + 10}
						stroke="#86bc25"
						opacity={0.5}
					/>
					<text
						x={x - offset}
						y={data[index].sales_Prev < data[index].sales_Curr ?  y - offset - 5 : y + offset + 15} 
						fill="#86bc25"
						className="graph-label-small"
						opacity={`${page == "summary" ? 0.8 : 1}`}
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};

const SalesGrowth = (props: Props) => {
	let data: any = props?.chartData?.finalData || [];
	const { page } = props;
	let minValue = 0;
	let maxValue = 0;
	let yoy: any = props?.chartData?.yoy;
	if (data?.length > 0) {
		const minPrev = data.reduce((min: any, obj: any) => {
			return obj.sales_Prev < min ? obj.sales_Prev : min;
		}, Infinity);
		const minCurr = data.reduce((min: any, obj: any) => {
			return obj.sales_Curr < min ? obj.sales_Curr : min;
		}, Infinity);
		const maxPrev = data.reduce((max: any, obj: any) => {
			return obj.sales_Prev > max ? obj.sales_Prev : max;
		}, -Infinity);
		const maxCurr = data.reduce((max: any, obj: any) => {
			return obj.sales_Curr > max ? obj.sales_Curr : max;
		}, -Infinity);
		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;
		data = data.map((item: any, id: any, arr: any) => {
			item.formattedWeek = moment.utc(item.month).format("MMM");
			item.forecastedMonthInd = item.monthCovered;
			item.dottedNetCost = null;
			item.newNetCost_Curr = null;
			if (item.monthCovered) {
				item.newNetCost_Curr = item.sales_Curr;
				// Check if this is the last covered month before uncovered months
				if (id < arr.length - 1 && !arr[id + 1]?.monthCovered) {
					item.dottedNetCost = item.sales_Curr;
				}
			} else {
				item.dottedNetCost = item.sales_Curr;
			}
			// Always include first and last months in one of the series
			if (id === 0) {
				item.newNetCost_Curr = item.sales_Curr; // Ensure first point is in actuals
			}
			if (id === arr.length - 1) {
				item.dottedNetCost = item.sales_Curr; // Ensure last point is in forecast
			}
			// Correct range calculations for shading between lines
			if (item.sales_Curr > item.sales_Prev) {
				item.greenRange = [item.sales_Prev, item.sales_Curr];
				item.redRange = [item.sales_Prev, item.sales_Prev];
			} else if (item.sales_Prev > item.sales_Curr) {
				item.greenRange = [item.sales_Curr, item.sales_Curr];
				item.redRange = [item.sales_Curr, item.sales_Prev];
			} else {
				item.greenRange = [item.sales_Prev, item.sales_Prev];
				item.redRange = [item.sales_Prev, item.sales_Prev];
			}
			return item;
		});
	}
	let heading = props?.heading;

	const footerData = [
		{ category: "2025" },
		{ category: "2026" },
		{ category: "2026F" },
	];

	const updateFill = (category: string) => {
		switch (category) {
			case "2025":
				return { color: "#0097a9", style: "solid" };
			case "2026":
				return { color: "#86bc25", style: "solid" };
			case "2026F":
				return { color: "#86bc25", style: "dashed" };
			default:
				return { color: "black", style: "solid" };
		}
	};

	const updateLegend = (category: string) => {
		return category;
	};
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "12vw",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				cursor: "pointer",
				color: "black",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-between h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">Month</div>
						<div className="font-bold" style={{ color: "#3f3f3f" }}>
							{data.formattedWeek}
						</div>
					</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Sales 2025:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.sales_Prev, 2, "$")}
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Sales 2026:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.sales_Curr, 2, "$")}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="w-full h-full px-3">
			{/* <div className="headSpace pb-2 !h-[10%]">
				<div className="headContent min-w-[12vw]">Sales ($)</div>
				<span
					className="bg-[#4d4d4d] w-full h-[0] flex justify-end px-[2vh] font-semibold text-[1.8vh]"
					style={{ color: yoy > 0 ? "#40a108" : "#a10808" }}
				>
					Growth YOY : {numberFormatter(yoy * 100, 1)}%
				</span>
			</div> */}
			<div className="headSpace !h-[6vh]">
				<span className="headContent"> Sales ($)</span>
				<p
					className="flex justify-end px-[2vh] font-semibold textNormal mt-[0] text-[#40a108]"
					style={{ color: yoy > 0 ? "#40a108" : "#a10808" }}
				>
					Growth YOY : {numberFormatter(yoy * 100, 1)}%
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height={props.isVisible ? "69%" : "77%"}
				className="bg-[#ffff] px-[1vh]"
			>
				<ComposedChart
					data={data}
					margin={{ top: 20, right: 25, left: 20 }}
				>
					<XAxis
						dataKey="formattedWeek"
						className="graph-label"
						stroke="black"
						interval={0}
					/>
					<YAxis
						hide
						domain={[minValue - minValue / 4, maxValue]}
						fontSize="1.5vh"
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>

					{/* Area components for shading between lines */}
					<Area
						dataKey="greenRange"
						stroke="#dce0a7"
						fill="#dce0a7"
						isAnimationActive={false}
					/>
					<Area
						dataKey="redRange"
						stroke="#ffcccc"
						fill="#ffcccc"
						isAnimationActive={false}
					/>
					<Line
						type="linear"
						dataKey="sales_Prev"
						name={`2025`}
						stroke="#0097a9"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={data}
											color={"#0097a9"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="sales_Prev"
							content={($) => (
								<CustomLabel
									payload={$}
									page={page}
									dataLength={data.length}
									data={data}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="newNetCost_Curr"
						stroke="#86bc25"
					name={`2026`}
						strokeDasharray={
							data?.formattedWeek >= "Jul" ? "5 5" : "0"
						}
						connectNulls={true}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={data}
											color={"#86bc25"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="sales_Curr"
							content={($) => (
								<CustomLabel2
									payload={$}
									last={false}
									page={page}
									dataLength={data.length}
									data={data}
								/>
							)}
						/>
					</Line>
					<Line
						type="monotone"
						dataKey="dottedNetCost"
						stroke="#86bc25"
					name={`2026F`}
						strokeDasharray="5 5"
						connectNulls={true}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={data}
											color={"#86bc25"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="sales_Curr"
							content={($) => (
								<CustomLabel2
									payload={$}
									last={true}
									page={page}
									dataLength={data.length}
									data={data}
								/>
							)}
						/>
					</Line>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter h-[12vh]">
				<div className="flex flex-wrap items-start gap-x-2 gap-y-2 p-4 w-full">
					{footerData.map((entry, index) => {
						const { color, style } = updateFill(entry.category);
						return (
							<div
								key={`legend-${index}`}
								className="flex items-center min-h-[20px] cursor-pointer"
							>
								{style === "solid" ? (
									<div
										className="w-3 h-3 mr-2"
										style={{ backgroundColor: color }}
									></div>
								) : (
									<div className="flex items-center mr-2">
										<div
											className="w-2 h-1"
											style={{
												backgroundColor: color,
												marginRight: "0.25rem",
												marginLeft: "0.25rem",
											}}
										></div>
										<div
											className="w-2 h-1"
											style={{
												backgroundColor: color,
												marginRight: "0.25rem",
											}}
										></div>
										<div
											className="w-2 h-1"
											style={{
												backgroundColor: color,
												marginRight: "0.25rem",
											}}
										></div>
										<div
											className="w-2 h-1"
											style={{ backgroundColor: color }}
										></div>
									</div>
								)}
								<span
									className="text-[0.85em] leading-tight"
									style={{ color }}
								>
									{updateLegend(entry.category)}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

const CustomLegend = (props: any) => {
	const { payload } = props.payload;
	return (
		<ul className="custom-legend flex justify-center">
			{payload.map((entry: any, index: any) => (
				<>
					{entry?.value !== "range" &&
						entry?.value !== "dotValue" &&
						entry?.value !== "greenRange" &&
						entry?.value !== "redRange" && (
							<li
								key={`item-${index}`}
								style={{
									color:
										entry.value === "2026" ||
										entry.value === "2026F"
											? "#86bc25"
											: "#0097a9",
									marginLeft: 5,
									fontSize: "1.8vh",
								}}
							>
								{entry.value !== "2026F" && (
									<span
										style={{
											backgroundColor:
												entry.value === "2026"
													? "#86bc25"
													: "#0097a9",
											width: "1.3vh",
											height: 2,
											display: "inline-block",
											marginRight: 5,
											top: -4,
											position: "relative",
										}}
									/>
								)}
								{entry.value === "2026F" && (
									<>
										<span
											style={{
												backgroundColor: "#86bc25",
												width: "0.4vh",
												height: 2,
												display: "inline-block",
												marginRight: 5,
												top: -4,
												position: "relative",
											}}
										/>
										<span
											style={{
												backgroundColor: "#86bc25",
												width: "0.4vh",
												height: 2,
												display: "inline-block",
												marginRight: 5,
												top: -4,
												position: "relative",
											}}
										/>
									</>
								)}
								{entry.value}
							</li>
						)}
				</>
			))}
		</ul>
	);
};

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color, dataKey } = props;
	// Logic to show dots at the start of actuals (newNetCost_Curr) and end of forecast (dottedNetCost)
	if (
		(index === 0 && dataKey === "newNetCost_Curr") ||
		(index === data.length - 1 && dataKey === "dottedNetCost")
	) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default SalesGrowth;
